import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { genericCopy } from '../utils/userFacingCopy';

export interface ExchangeRateInfo {
  zarToGbpRate: number;
  lastRefreshed: string | null;
  source: string;
  sourceUrl: string;
  usingFallbackRates: boolean;
}

/** Cache exchange rates for 1 hour; they don't change that often and we want to avoid unnecessary API calls. */
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
let cached: { data: ExchangeRateInfo; at: number } | null = null;

export function useExchangeRates(): {
  rates: ExchangeRateInfo | null;
  loading: boolean;
  error: string | null;
  convertToGbp: (amount: number, currencyCode: string) => number;
} {
  const [rates, setRates] = useState<ExchangeRateInfo | null>(cached?.data ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cached && Date.now() - cached.at <= CACHE_TTL_MS) {
      queueMicrotask(() => {
        setRates(cached!.data);
        setLoading(false);
      });
      return;
    }
    queueMicrotask(() => {
      setLoading(true);
      setError(null);
    });
    apiService
      .get<ExchangeRateInfo>(API_ENDPOINTS.SYSTEM.EXCHANGE_RATES)
      .then((res) => {
        const data = res.data;
        cached = { data, at: Date.now() };
        setRates(data);
      })
      .catch(() => {
        setError(genericCopy.exchangeRates);
        setRates(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const convertToGbp = useCallback(
    (amount: number, currencyCode: string): number => {
      if (!currencyCode || currencyCode.toUpperCase() === 'GBP') return amount;
      if (!rates) return amount; // no rates yet
      if (currencyCode.toUpperCase() === 'ZAR') return amount * rates.zarToGbpRate;
      return amount;
    },
    [rates]
  );

  return { rates, loading, error, convertToGbp };
}
